import React, { useState } from "react";
import { Subheading } from "../../../../design/heading";
import { Button } from "../../../../design/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../../../design/dialog";
import { Textarea } from "../../../../design/textarea";
import { Field, Fieldset, FieldGroup, Label } from "../../../../design/fieldset";
import { useParent } from "../../../../contexts/ParentContext";
import { ParentNote } from "@visitproof/types";
import { Divider } from "../../../../design/divider";
import { PlusIcon, TrashIcon, PencilIcon } from "@heroicons/react/16/solid";
import moment from "moment";

interface Props {
  parentId: string;
}

const ParentNotesCard: React.FC<Props> = ({ parentId }) => {
  const { notes, addNoteToParent, updateParentNote, deleteNoteFromParent } = useParent();
  const [isOpen, setIsOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<ParentNote | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [content, setContent] = useState("");

  const resetForm = () => {
    setContent("");
    setEditingNote(null);
  };

  const openAddDialog = () => {
    resetForm();
    setIsOpen(true);
  };

  const openEditDialog = (note: ParentNote) => {
    setContent(note.content);
    setEditingNote(note);
    setIsOpen(true);
  };

  const handleSubmit = async () => {
    if (!content.trim()) return;

    setIsSubmitting(true);
    try {
      if (editingNote) {
        await updateParentNote(parentId, editingNote.id, content.trim());
      } else {
        await addNoteToParent(parentId, content.trim());
      }
      setIsOpen(false);
      resetForm();
    } catch (error) {
      console.error("Error saving note:", error);
    }
    setIsSubmitting(false);
  };

  const handleDelete = async (note: ParentNote) => {
    if (!window.confirm("Are you sure you want to delete this note?")) return;

    setIsSubmitting(true);
    try {
      await deleteNoteFromParent(parentId, note.id);
    } catch (error) {
      console.error("Error deleting note:", error);
    }
    setIsSubmitting(false);
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return "";
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return moment(date).format("MMM D, YYYY [at] h:mm A");
  };

  return (
    <div className="p-6 bg-white border rounded-xl shadow-sm dark:bg-zinc-900 border-zinc-200 dark:border-zinc-700">
      <div className="flex items-center justify-between">
        <Subheading>Case Notes</Subheading>
        <Button outline onClick={openAddDialog}>
          <PlusIcon className="w-4 h-4 mr-1" />
          Add Note
        </Button>
      </div>
      <Divider className="my-4" />

      {notes.length === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            No notes yet. Add notes to keep track of important information about this client.
          </p>
        </div>
      ) : (
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {notes.map((note) => (
            <div
              key={note.id}
              className="p-4 bg-zinc-50 dark:bg-zinc-800 rounded-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-zinc-900 dark:text-zinc-100 whitespace-pre-wrap">
                    {note.content}
                  </p>
                  <div className="mt-2 text-xs text-zinc-500 dark:text-zinc-400">
                    <span>{formatDate(note.createdAt)}</span>
                    {note.createdByName && (
                      <span> by {note.createdByName}</span>
                    )}
                    {note.updatedAt && note.updatedAt !== note.createdAt && (
                      <span className="ml-2 italic">(edited)</span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1 ml-4">
                  <Button
                    plain
                    onClick={() => openEditDialog(note)}
                    disabled={isSubmitting}
                  >
                    <PencilIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    plain
                    onClick={() => handleDelete(note)}
                    disabled={isSubmitting}
                  >
                    <TrashIcon className="w-4 h-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Note Dialog */}
      <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
        <DialogTitle>
          {editingNote ? "Edit Note" : "Add Note"}
        </DialogTitle>
        <DialogBody>
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>Note</Label>
                <Textarea
                  value={content}
                  placeholder="Enter your case note here..."
                  onChange={(e) => setContent(e.target.value)}
                  rows={5}
                  autoFocus
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button plain onClick={() => setIsOpen(false)}>
            Cancel
          </Button>
          <Button
            disabled={isSubmitting || !content.trim()}
            onClick={handleSubmit}
          >
            {isSubmitting ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ParentNotesCard;
